// "Edit Filters" modal — build advanced candidate filters CEIPAL-style: add
// rows of Field + Operator + Value, AND-ed together. Apply pushes them to the
// list query; they can then be saved into a view.

import { useState } from 'react';
import {
  FILTER_FIELDS, OPERATORS, fieldDef, defaultOp, isValuelessOp,
  type CandidateFilterRow,
} from './candidateFilters';
// Phase 2 design system
import { Modal, Button, Select, Input, Icon } from '../../components/ui';

export default function CandidateFiltersModal({ initial, onApply, onClose }: {
  initial: CandidateFilterRow[]; onApply: (rows: CandidateFilterRow[]) => void; onClose: () => void;
}) {
  const [rows, setRows] = useState<CandidateFilterRow[]>(
    initial.length ? initial : [{ field: FILTER_FIELDS[0].key, op: defaultOp(FILTER_FIELDS[0].type), value: '' }]
  );

  function update(i: number, patch: Partial<CandidateFilterRow>) {
    setRows((prev) => prev.map((r, idx) => {
      if (idx !== i) return r;
      const next = { ...r, ...patch };
      // When the field changes, reset the operator + value to sane defaults.
      if (patch.field) { const d = fieldDef(patch.field); next.op = d ? defaultOp(d.type) : 'contains'; next.value = ''; }
      return next;
    }));
  }
  const addRow = () => setRows((p) => [...p, { field: FILTER_FIELDS[0].key, op: defaultOp(FILTER_FIELDS[0].type), value: '' }]);
  const removeRow = (i: number) => setRows((p) => p.filter((_, idx) => idx !== i));

  function apply() {
    // Drop incomplete rows (no value unless the op is valueless).
    const clean = rows.filter((r) => r.field && (isValuelessOp(r.op) || String(r.value).trim() !== ''));
    onApply(clean);
    onClose();
  }

  return (
    <Modal open onClose={onClose} title="Edit Filters" size="lg">
      <p style={{ fontSize: 12, color: 'var(--t3)', marginTop: -6, marginBottom: 16 }}>
        All conditions must match (AND). Apply to filter the list, then “Save current filters” to keep it as a view.
      </p>

      {rows.map((r, i) => {
        const d = fieldDef(r.field);
        const type = d?.type ?? 'text';
        return (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
            <Select aria-label="Filter field" style={{ minWidth: 150 }} value={r.field} onChange={(e) => update(i, { field: e.target.value })}>
              {FILTER_FIELDS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </Select>
            <Select aria-label="Filter operator" style={{ minWidth: 130 }} value={r.op} onChange={(e) => update(i, { op: e.target.value })}>
              {OPERATORS[type].map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
            {!isValuelessOp(r.op) && (
              (d?.type === 'enum' || d?.type === 'bool') && d.options ? (
                <Select aria-label="Filter value" style={{ flex: 1, minWidth: 100 }} value={r.value} onChange={(e) => update(i, { value: e.target.value })}>
                  <option value="">Select…</option>
                  {d.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
              ) : d?.type === 'number' || (d?.type === 'date' && r.op === 'last_n_days') ? (
                <Input aria-label="Filter value" style={{ flex: 1, minWidth: 100 }} type="number" value={r.value} onChange={(e) => update(i, { value: e.target.value })} placeholder="value" />
              ) : d?.type === 'date' ? (
                <Input aria-label="Filter value" style={{ flex: 1, minWidth: 100 }} type="date" value={r.value} onChange={(e) => update(i, { value: e.target.value })} />
              ) : (
                <Input aria-label="Filter value" style={{ flex: 1, minWidth: 100 }} value={r.value} onChange={(e) => update(i, { value: e.target.value })} placeholder="value" />
              )
            )}
            <Button variant="ghost" size="sm" iconOnly aria-label="Remove filter" title="Remove" onClick={() => removeRow(i)}>
              <Icon name="x" size={16} />
            </Button>
          </div>
        );
      })}

      <Button variant="secondary" size="sm" icon={<Icon name="plus" size={15} />} onClick={addRow} style={{ marginTop: 4 }}>
        Add Filter
      </Button>

      <Modal.Footer>
        <Button variant="primary" onClick={apply}>Apply filters</Button>
        <Button variant="secondary" onClick={() => { onApply([]); onClose(); }}>Clear all</Button>
      </Modal.Footer>
    </Modal>
  );
}
